import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import styles from './css/Services.module.css';

const fadeUp = {
  hidden:  { opacity: 0, y: 36 },
  visible: { opacity: 1, y: 0 },
};

const SERVICES = [
  {
    icon: '🌐',
    title: 'Web Development',
    desc: 'Modern, responsive web applications engineered for performance and scalability. We build everything from fast marketing sites to complex enterprise portals — always with clean, maintainable code.',
    items: [
      'Single Page Applications (SPAs)',
      'Progressive Web Apps (PWAs)',
      'E-commerce Platforms',
      'Custom Dashboards & Admin Panels',
      'CMS Integration',
    ],
  },
  {
    icon: '📱',
    title: 'Mobile Development',
    desc: 'Native-quality iOS and Android apps built with React Native. One codebase, two platforms — without compromising on feel, speed, or functionality.',
    items: [
      'Cross-Platform React Native Apps',
      'iOS & Android Development',
      'App Store / Google Play Deployment',
      'Offline-First Architecture',
      'Push Notifications & Deep Linking',
    ],
  },
  {
    icon: '⚙️',
    title: 'Full-Stack Solutions',
    desc: 'End-to-end ownership from database schema to polished UI. We design and build complete systems — robust, secure, and built to grow with your business.',
    items: [
      'RESTful & GraphQL API Design',
      'Database Architecture & Optimisation',
      'Authentication, Roles & Security',
      'Cloud Deployment (AWS, Azure, GCP)',
      'Microservices & Serverless',
    ],
  },
  {
    icon: '🔗',
    title: 'Software Integration',
    desc: 'Connect your existing tools, platforms, and data sources. We eliminate silos and automate workflows so your systems work as one coherent whole.',
    items: [
      'Third-Party API Integration',
      'Legacy System Modernisation',
      'Workflow & Process Automation',
      'Data Migration & Transformation',
      'Webhook & Event-Driven Architecture',
    ],
  },
  {
    icon: '🧠',
    title: 'Technical Consulting',
    desc: 'Drawing on 35+ years of industry experience, we provide strategic guidance on architecture, technology choices, code quality, and team capability.',
    items: [
      'Architecture Reviews & Roadmaps',
      'Technology Stack Selection',
      'Code Audits & Refactoring Plans',
      'Performance & Scalability Analysis',
      'DevOps & CI/CD Strategy',
    ],
  },
  {
    icon: '🛡️',
    title: 'Ongoing Support & Maintenance',
    desc: 'Long-term partnerships, not just project hand-offs. We provide continued support, monitoring, updates, and iterative improvements after launch.',
    items: [
      'Bug Fixes & Patch Releases',
      'Performance Monitoring',
      'Security Updates & Audits',
      'Feature Iteration',
      'SLA-based Support Agreements',
    ],
  },
];

const PROCESS = [
  { title: 'Discovery',    desc: 'We listen first. Understanding your goals, constraints, and users before writing a single line of code.' },
  { title: 'Proposal',     desc: 'A clear scope, timeline, and cost breakdown. No surprises — just honest expectations.' },
  { title: 'Design',       desc: 'Wireframes, architecture plans, and UX thinking before development begins.' },
  { title: 'Build',        desc: 'Iterative development with regular check-ins. You see progress throughout, not just at the end.' },
  { title: 'Test & QA',    desc: 'Thorough testing across devices, browsers, and edge cases before anything goes live.' },
  { title: 'Launch',       desc: 'Smooth deployment with zero-downtime strategies, monitoring, and your team fully briefed.' },
];

const WHY = [
  { icon: '🏆', title: '35+ Years Experience',   text: 'Decades of real-world delivery across industries — from startups to enterprise.' },
  { icon: '🤝', title: 'Honest Communication',    text: 'Plain language, realistic timelines, and no jargon. You always know where things stand.' },
  { icon: '🔬', title: 'Quality at Every Layer',  text: 'Clean architecture, readable code, and tested software you can confidently build on.' },
  { icon: '📈', title: 'Built to Scale',          text: 'Solutions designed to grow with your business — not re-built from scratch in two years.' },
  { icon: '⚡', title: 'Modern Tooling',          text: 'Up-to-date with the latest frameworks, cloud platforms, and best practices.' },
  { icon: '🔒', title: 'Security-First',          text: 'Security is not an afterthought. We follow OWASP guidelines and best practices by default.' },
];

function Services() {
  return (
    <div className={styles.page}>

      {/* ── Header ── */}
      <motion.div
        className={styles.pageHeader}
        initial="hidden"
        animate="visible"
        variants={fadeUp}
        transition={{ duration: 0.7 }}
      >
        <span className={styles.sectionLabel}>What We Offer</span>
        <div className={styles.divider} />
        <h1 className={styles.pageTitle}>Our Services</h1>
        <p className={styles.pageSubtitle}>
          Full-stack expertise across web, mobile, and enterprise software. Whatever stage your project is at,
          we have the skills and experience to deliver it well.
        </p>
      </motion.div>

      {/* ── Service cards grid ── */}
      <div className={styles.heroGrid}>
        {SERVICES.map((s, i) => (
          <motion.div
            key={i}
            className={styles.heroCard}
            initial="hidden"
            whileInView="visible"
            variants={fadeUp}
            transition={{ duration: 0.5, delay: i * 0.08 }}
            viewport={{ once: true, amount: 0.1 }}
          >
            <div className={styles.cardIconWrap}>{s.icon}</div>
            <h2 className={styles.cardTitle}>{s.title}</h2>
            <p className={styles.cardDesc}>{s.desc}</p>
            <ul className={styles.cardList}>
              {s.items.map((item, j) => <li key={j}>{item}</li>)}
            </ul>
            <Link to="/contact" className={styles.cardCta}>
              Enquire about this service →
            </Link>
          </motion.div>
        ))}
      </div>

      {/* ── How we work ── */}
      <motion.div
        className={styles.processSection}
        initial="hidden"
        whileInView="visible"
        variants={fadeUp}
        transition={{ duration: 0.6 }}
        viewport={{ once: true, amount: 0.15 }}
      >
        <div className={styles.processSectionHeader}>
          <span className={styles.sectionLabel}>How We Work</span>
          <div className={styles.divider} />
          <h2 className={styles.pageTitle} style={{ fontSize: 'clamp(1.6rem, 3vw, 2.2rem)' }}>
            Our Process
          </h2>
          <p className={styles.pageSubtitle}>
            A clear, collaborative process from first conversation to live product.
          </p>
        </div>

        <div className={styles.processSteps}>
          {PROCESS.map((step, i) => (
            <motion.div
              key={i}
              className={styles.processStep}
              initial="hidden"
              whileInView="visible"
              variants={fadeUp}
              transition={{ duration: 0.45, delay: i * 0.07 }}
              viewport={{ once: true, amount: 0.15 }}
            >
              <div className={styles.stepNumber}>{i + 1}</div>
              <h3 className={styles.stepTitle}>{step.title}</h3>
              <p className={styles.stepDesc}>{step.desc}</p>
            </motion.div>
          ))}
        </div>
      </motion.div>

      {/* ── Why Byte79 ── */}
      <section className={styles.whySection}>
        <motion.div
          initial="hidden"
          whileInView="visible"
          variants={fadeUp}
          transition={{ duration: 0.6 }}
          viewport={{ once: true, amount: 0.15 }}
        >
          <div className={styles.processSectionHeader}>
            <span className={styles.sectionLabel}>Why Choose Us</span>
            <div className={styles.divider} />
            <h2 className={styles.pageTitle} style={{ fontSize: 'clamp(1.6rem, 3vw, 2.2rem)' }}>
              What Sets Byte79 Apart
            </h2>
          </div>
        </motion.div>

        <div className={styles.whyGrid}>
          {WHY.map((w, i) => (
            <motion.div
              key={i}
              className={styles.whyCard}
              initial="hidden"
              whileInView="visible"
              variants={fadeUp}
              transition={{ duration: 0.45, delay: i * 0.07 }}
              viewport={{ once: true, amount: 0.1 }}
            >
              <span className={styles.whyIcon}>{w.icon}</span>
              <h3 className={styles.whyTitle}>{w.title}</h3>
              <p className={styles.whyText}>{w.text}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ── CTA strip ── */}
      <motion.div
        className={styles.ctaStrip}
        initial="hidden"
        whileInView="visible"
        variants={fadeUp}
        transition={{ duration: 0.7 }}
        viewport={{ once: true, amount: 0.3 }}
      >
        <h2 className={styles.ctaTitle}>Ready to get started?</h2>
        <p className={styles.ctaText}>
          Tell us about your project and we'll come back to you within one business day.
        </p>
        <div className={styles.ctaButtons}>
          <Link to="/contact" className={styles.btnPrimary}>Start a Project →</Link>
          <Link to="/about"   className={styles.btnSecondary}>Learn About Us</Link>
        </div>
      </motion.div>

    </div>
  );
}

export default Services;
